import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template26: Página Kiyomizudera-2 (Gion y Yasaka) - Kioto
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template26 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const transportText = page?.texts?.[1]?.content;
	const kodaijiText = page?.texts?.[2]?.content;
	const yasakaTitle = page?.texts?.[3]?.content;
	const yasakaText = page?.texts?.[4]?.content;
	const maruyamaText = page?.texts?.[5]?.content;
	const gionIntroText = page?.texts?.[6]?.content;
	const gionTitle = page?.texts?.[7]?.content;
	const gionText = page?.texts?.[8]?.content;
	const text9 = page?.texts?.[9];
	const gionMatsuriText =
		isSmallScreen && text9?.contentMovil ? text9.contentMovil : text9?.content;
	const geikoText = page?.texts?.[10]?.content;
	const shirakawaText = page?.texts?.[11]?.content;
	const sabiasQueMaruyama1 = parseSabiasQueItem(page?.texts?.[12]?.content);
	const sabiasQueMaruyama2 = parseSabiasQueItem(page?.texts?.[13]?.content);
	const sabiasQueGion1 = parseSabiasQueItem(page?.texts?.[14]?.content);
	const sabiasQueGion2 = parseSabiasQueItem(page?.texts?.[15]?.content);
	const sabiasQueGion3 = parseSabiasQueItem(page?.texts?.[16]?.content);

	const logoImage = getCanvasImageUrl("logo");
	const yasakaImage = getImageSrc("yasaka");
	const gionImage = getImageSrc("gion");
	const yasakaSticker = getCanvasImageUrl("yasaka-sticker");
	const gionSticker = getCanvasImageUrl("gion-sticker");

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
		color: contentColor,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& p:not([style*='marginBottom'])": {
					marginBottom: "0px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* TRANSPORTE */}
			{transportText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative" }}>
					<Box
						sx={{
							backgroundColor: "#e8f3ee",
							border: "3px solid #5ba374",
							borderRadius: "12px",
							padding: "16px 20px",
							fontSize: "12px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							color: "#333",
							marginTop: "8px",
						}}
						dangerouslySetInnerHTML={{ __html: transportText }}
					/>
				</Box>
			)}

			{/* KODAIJI */}
			{kodaijiText && (
				<Box data-block-id="text-2" sx={{ padding: "10px 12px", position: "relative" }}>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "13px",
							color: "#333",
							margin: "0 !important",
							padding: 0,
							marginBottom: "20px !important",
						}}
						dangerouslySetInnerHTML={{ __html: kodaijiText }}
					/>

					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: "16px",
						}}
					>
						{/* YASAKA */}
						<Box sx={{ display: "flex", gap: "16px", marginTop: "12px" }}>
							{yasakaImage && (
								<Box
									component="img"
									src={yasakaImage}
									alt="Yasaka"
									sx={{
										width: "40%",
										height: "110px",
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{yasakaTitle && (
									<Box
										component="h3"
										sx={estiloH3}
										dangerouslySetInnerHTML={{ __html: yasakaTitle }}
									/>
								)}
								{yasakaText && (
									<Box
										component="p"
										sx={{
											...estiloP,
											marginTop: "0px !important",
											marginBottom: "0px !important",
										}}
										dangerouslySetInnerHTML={{ __html: yasakaText }}
									/>
								)}
							</Box>
						</Box>

						{/* MARUYAMA */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: "16px",
								position: "relative",
							}}
						>
							{maruyamaText && (
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "18px",
										backgroundColor: "#d9e6de",
										borderRadius: "12px",
										width: "70%",
										margin: "0 !important",
									}}
									dangerouslySetInnerHTML={{ __html: maruyamaText }}
								/>
							)}
							{sabiasQueMaruyama1 && (
								<Box sx={{ position: "absolute", top: "-12px", right: "50%" }}>
									<SabiasQue
										icono={sabiasQueMaruyama1.icono}
										texto={sabiasQueMaruyama1.texto}
									/>
								</Box>
							)}

							{sabiasQueMaruyama2 && (
								<Box
									sx={{
										position: "absolute",
										bottom: "-12px",
										left: "45%",
									}}
								>
									<SabiasQue
										icono={sabiasQueMaruyama2.icono}
										texto={sabiasQueMaruyama2.texto}
									/>
								</Box>
							)}

							<Box
								sx={{
									position: "relative",
									width: "30%",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								{gionMatsuriText && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? "-80px" : "-100px",
											left: 0,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 0px 16px",
											padding: "10px 12px",
											fontSize: "11px",
											fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: "200px",
											zIndex: 3,
										}}
										dangerouslySetInnerHTML={{ __html: gionMatsuriText }}
									/>
								)}
								{yasakaSticker && (
									<Box
										component="img"
										src={yasakaSticker}
										alt="Yasaka"
										sx={{
											width: "110px",
											position: "absolute",
											zIndex: 2,
											top: isSmallScreen ? "-40px" : "-60px",
											right: "-20px",
										}}
									/>
								)}
							</Box>
						</Box>
					</Box>
				</Box>
			)}

			{/* GION */}
			{gionIntroText && (
				<Box
					data-block-id="text-3"
					sx={{
						padding: "10px 12px",
						position: "relative",
						marginTop: "0px !important",
					}}
				>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "13px",
							color: "#333",
							margin: "0 !important",
							padding: 0,
							marginTop: "0px !important",
							marginBottom: "20px !important",
						}}
						dangerouslySetInnerHTML={{ __html: gionIntroText }}
					/>

					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							position: "relative",
							gap: "16px",
						}}
					>
						{sabiasQueGion1 && (
							<Box sx={{ position: "absolute", top: "2px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQueGion1.icono}
									texto={sabiasQueGion1.texto}
								/>
							</Box>
						)}
						{sabiasQueGion2 && (
							<Box sx={{ position: "absolute", top: "40px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQueGion2.icono}
									texto={sabiasQueGion2.texto}
								/>
							</Box>
						)}
						<Box sx={{ display: "flex", gap: "16px", marginTop: "12px" }}>
							{gionImage && (
								<Box
									component="img"
									src={gionImage}
									alt="Gion"
									sx={{
										width: "40%",
										height: "110px",
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{gionTitle && (
									<Box
										component="h3"
										sx={estiloH3}
										dangerouslySetInnerHTML={{ __html: gionTitle }}
									/>
								)}
								{gionText && (
									<Box
										component="p"
										sx={{
											...estiloP,
											marginTop: "0px !important",
											marginBottom: "0px !important",
										}}
										dangerouslySetInnerHTML={{ __html: gionText }}
									/>
								)}
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "16px",
							position: "relative",
						}}
					>
						<Box
							sx={{
								position: "relative",
								width: "40%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{geikoText && (
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? "-120px" : "-70px",
										left: "40px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: "11px",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: "200px",
										zIndex: 3,
									}}
									dangerouslySetInnerHTML={{ __html: geikoText }}
								/>
							)}

							{gionSticker && (
								<Box
									component="img"
									src={gionSticker}
									alt="GionSticker"
									sx={{
										width: "100px",
										position: "absolute",
										zIndex: 2,
										bottom: "-50px",
										left: "-20px",
									}}
								/>
							)}
						</Box>

						{sabiasQueGion3 && (
							<Box
								sx={{
									position: "absolute",
									bottom: 0,
									left: "33%",
								}}
							>
								<SabiasQue
									icono={sabiasQueGion3.icono}
									texto={sabiasQueGion3.texto}
									position="above"
								/>
							</Box>
						)}

						{shirakawaText && (
							<Box
								component="p"
								sx={{
									...estiloP,
									padding: "12px",
									backgroundColor: "#d9e6de",
									borderRadius: "12px",
									width: "60%",
									margin: "0 !important",
									marginTop: isSmallScreen ? "20px !important" : "0 !important",
								}}
								dangerouslySetInnerHTML={{ __html: shirakawaText }}
							/>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template26.thumbnail = "/images/template-1.png";

export default Template26;
